import React, { ReactElement } from 'react';
import InputTooltip from '../../../ui/layout/form/input/InputTooltip';
import { get, set } from 'lodash';
import useInputHelper from '../inputHelper';
import { useFormContext } from '../../FormContext';
import { useUiTheme } from 'framework/uiTheme';
import VerticalCopyableInput from '../VerticalCopyableInput';
import { Box, Typography } from '@mui/material';

interface CopyableInputProps {
  source: string;
  buttonText?: string;
  copySource: any;
  copySourceList: string[];
  tooltipMessage?: string;
  className?: TypeOrFunction<string>;
  children: ReactElement;
  label?: string | JSX.Element;
}

const CopyableInput = ({
  buttonText,
  copySource,
  copySourceList,
  tooltipMessage,
  children,
  ...props
}: CopyableInputProps) => {
  const { getSource, getLabel, getRawOnChangeCallback } = useInputHelper(props);
  const { data } = useFormContext();
  const rawOnchangeCallback = getRawOnChangeCallback();
  const source = getSource();
  const { Button } = useUiTheme();

  const setValues = () => {
    const updateObj = { ...data };

    copySourceList.forEach(key => {
      const copiedValue = get(data, `${copySource}.${key}`);
      set(updateObj, `${source}.${key}`, copiedValue);
    });

    if (rawOnchangeCallback) {
      rawOnchangeCallback(updateObj);
    }
  };

  return (
    <>
      <Box
        sx={theme => ({
          display: 'flex',
          alignItems: 'center',
          margin: '8px 0 10px',
          fontSize: '18px',
          fontWeight: 400,
          [theme.breakpoints.down('smallMobile')]: {
            display: 'block',
          },
        })}
      >
        <Typography variant="h3" sx={{ margin: '0 10px 0 0', fontSize: '18px', fontWeight: 400 }}>
          {getLabel() as string}
        </Typography>

        <Button
          size="small"
          variant="text"
          onClick={setValues}
          sx={theme => ({
            transform: 'translateY(1px)',
            marginRight: '10px',
            [theme.breakpoints.down('smallMobile')]: {
              lineHeight: 1.5,
              marginBottom: '7px',
            },
          })}
        >
          {buttonText}
        </Button>
        {tooltipMessage && (
          <InputTooltip
            size="small"
            message={tooltipMessage}
            source={source}
            sx={theme => ({
              transform: 'translateY(1px)',
              [theme.breakpoints.down('smallMobile')]: {
                transform: 'translateY(4px)',
              },
            })}
          />
        )}
      </Box>
      <VerticalCopyableInput input={children} />
    </>
  );
};

export default CopyableInput;
